import React, { useState, useRef, useEffect, useCallback } from "react";
import { GripVertical, MoreHorizontal } from "lucide-react";

/**
 * Usable — hover ⋯ menu for rename / copy / text-select; grip for drag.
 * Text is not selectable unless toggled from the menu.
 */
export default function Usable({
  children,
  location = [],
  onRename,
  onCopy,
  copyLabel = "Copiar ruta",
  copyValue,
  dragHandleProps,
  onClick,
  style,
  bodyStyle,
  className = "",
  title,
  /** Extra ⋯ actions: [{ label, onClick, danger? }] */
  menuItems = [],
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [textSelectable, setTextSelectable] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const rootRef = useRef(null);

  const locationPath = Array.isArray(location)
    ? location.filter(Boolean).join(" › ")
    : location || "";

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
        setRenaming(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const handleCopy = useCallback(async () => {
    const value = copyValue ?? locationPath;
    if (!value) return;
    try {
      if (onCopy) await onCopy(value);
      else await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("[Usable] copy failed:", err);
    }
    setMenuOpen(false);
  }, [copyValue, locationPath, onCopy]);

  const startRename = () => {
    setRenaming(true);
    setRenameValue(typeof title === "string" ? title : "");
  };

  const commitRename = async () => {
    const next = renameValue.trim();
    if (next && onRename) await onRename(next);
    setRenaming(false);
    setMenuOpen(false);
  };

  const handleRootClick = (e) => {
    if (e.target.closest(".usable-menu, .usable-grip, .usable-menu-btn")) return;
    onClick?.(e);
  };

  const classes = [
    "usable",
    textSelectable ? "usable--text-select" : "",
    menuOpen ? "usable--menu-open" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={rootRef}
      className={classes}
      style={style}
      onClick={handleRootClick}
      title={locationPath || undefined}
    >
      {dragHandleProps && (
        <div className="usable-grip" {...dragHandleProps} onClick={(e) => e.stopPropagation()}>
          <GripVertical size={16} />
        </div>
      )}

      <div className="usable-body" style={bodyStyle}>{children}</div>

      <button
        type="button"
        className="usable-menu-btn"
        aria-label="Acciones"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((o) => !o);
          setRenaming(false);
        }}
      >
        <MoreHorizontal size={16} />
      </button>

      {menuOpen && (
        <div className="usable-menu" onClick={(e) => e.stopPropagation()}>
          {locationPath && (
            <div className="usable-menu-location" title={locationPath}>
              <span className="usable-menu-location-label">Ubicación</span>
              {locationPath}
            </div>
          )}

          {renaming ? (
            <div className="usable-menu-rename">
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setRenaming(false);
                }}
                className="usable-menu-rename-input"
              />
              <button type="button" className="usable-menu-item" onClick={commitRename}>
                Guardar
              </button>
            </div>
          ) : (
            <>
              {onRename && (
                <button type="button" className="usable-menu-item" onClick={startRename}>
                  Renombrar
                </button>
              )}
              {(copyValue || locationPath || onCopy) && (
                <button type="button" className="usable-menu-item" onClick={handleCopy}>
                  {copyLabel}
                </button>
              )}
              {(menuItems || []).map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="usable-menu-item"
                  style={item.danger ? { color: "#c97a6a" } : undefined}
                  disabled={item.disabled}
                  onClick={async () => {
                    setMenuOpen(false);
                    await item.onClick?.();
                  }}
                >
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                className="usable-menu-item"
                onClick={() => {
                  setTextSelectable((s) => !s);
                  setMenuOpen(false);
                }}
              >
                {textSelectable ? "Bloquear selección" : "Seleccionar texto"}
              </button>
              {dragHandleProps && (
                <div className="usable-menu-hint">Arrastrá ⋮⋮ para reordenar</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
